(() => {
  const COMPANY_ID = 'Wv8RGm';
  const API_REVISION = '2026-04-15';
  const STORAGE_TTL_MS = 15 * 60 * 1000;
  const FORM_CONFIG = {
    support: {
      metric: 'Customer Support Submitted',
      properties: ['help_topic', 'message', 'privacy_consent']
    },
    business: {
      metric: 'Business Inquiry Submitted',
      properties: ['company', 'country_or_region', 'inquiry_type', 'project_summary', 'privacy_consent']
    }
  };

  window.__contactAbokKlaviyoRequests = window.__contactAbokKlaviyoRequests || new Set();

  const requestIds = window.__contactAbokKlaviyoRequests;

  const createUniqueId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  };

  const getStorageKey = (sectionId, formType) => `contact-abok:${sectionId}:${formType}`;

  const readStoredPayload = (key) => {
    try {
      const payload = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      if (!payload || !payload.expiresAt || payload.expiresAt <= Date.now()) {
        window.sessionStorage.removeItem(key);
        return null;
      }
      return payload;
    } catch (error) {
      return null;
    }
  };

  const writeStoredPayload = (key, payload) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(payload));
    } catch (error) {
      // Storage is progressive enhancement; Shopify submission must still continue.
    }
  };

  const clearStoredPayload = (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      // Ignore storage failures after a successful event request.
    }
  };

  const collectPayload = (form, formType) => {
    const config = FORM_CONFIG[formType];
    if (!config) return null;

    const email = form.querySelector('[data-klaviyo-profile="email"]')?.value.trim() || '';
    if (!email) return null;

    const properties = {
      form_type: formType,
      form_id: form.id,
      page_url: window.location.href.split('#')[0],
      submitted_at: new Date().toISOString()
    };

    config.properties.forEach((propertyName) => {
      const field = form.querySelector(`[data-klaviyo-property="${propertyName}"]`);
      if (!field) return;

      if (field.type === 'checkbox') {
        properties[propertyName] = field.checked;
        return;
      }

      const value = String(field.value);
      if (value.trim()) properties[propertyName] = value;
    });

    return {
      email,
      metric: config.metric,
      uniqueId: createUniqueId(),
      properties,
      expiresAt: Date.now() + STORAGE_TTL_MS
    };
  };

  const sendKlaviyoEvent = async (payload, storageKey, formType) => {
    if (!payload?.email || !payload?.metric || !payload?.uniqueId || requestIds.has(payload.uniqueId)) return;

    requestIds.add(payload.uniqueId);
    let response;

    try {
      response = await fetch(`https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(COMPANY_ID)}`, {
        method: 'POST',
        headers: {
          accept: 'application/vnd.api+json',
          'content-type': 'application/vnd.api+json',
          revision: API_REVISION
        },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              unique_id: payload.uniqueId,
              properties: payload.properties,
              metric: {
                data: {
                  type: 'metric',
                  attributes: { name: payload.metric }
                }
              },
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: payload.email }
                }
              }
            }
          }
        }),
        keepalive: true
      });

      if (!response.ok) {
        throw new Error('http_error');
      }

      clearStoredPayload(storageKey);
    } catch (error) {
      requestIds.delete(payload.uniqueId);
      console.warn('Unable to send contact event to Klaviyo.', {
        formType,
        status: response?.status || 0,
        errorType: error?.message === 'http_error' ? 'http_error' : 'network_error'
      });
    }
  };

  const getSubmittedFormId = () => {
    try {
      return decodeURIComponent(window.location.hash.slice(1));
    } catch (error) {
      return window.location.hash.slice(1);
    }
  };

  const clearSubmissionUrl = () => {
    const url = new URL(window.location.href);
    ['contact_posted', 'form_type', 'utf8'].forEach((parameter) => url.searchParams.delete(parameter));
    url.hash = '';
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}`);
  };

  const resetSubmittedForm = (form) => {
    form.reset();
    Array.from(form.elements).forEach((field) => {
      if (field.disabled || field.type === 'hidden') return;
      if (field instanceof HTMLSelectElement) {
        field.selectedIndex = 0;
      } else if (field.type === 'checkbox' || field.type === 'radio') {
        field.checked = false;
      } else if (!['button', 'submit', 'reset'].includes(field.type)) {
        field.value = '';
      }
    });
  };

  const handleFormResult = (form, wrapper) => {
    const hashMatches = getSubmittedFormId() === form.id;
    const hasResult = wrapper.dataset.postedSuccessfully === 'true' || wrapper.dataset.hasErrors === 'true';
    if (!hashMatches || !hasResult) return;

    resetSubmittedForm(form);
    clearSubmissionUrl();
  };

  const initializeSection = (section) => {
    if (section.dataset.contactAbokInitialized === 'true') return;
    section.dataset.contactAbokInitialized = 'true';

    const sectionId = section.dataset.sectionId || '';
    section.querySelectorAll('[data-contact-abok-form]').forEach((wrapper) => {
      const form = wrapper.closest('form');
      const formType = wrapper.dataset.contactAbokForm;
      const config = FORM_CONFIG[formType];
      if (!form || !config) return;

      const storageKey = getStorageKey(sectionId, formType);

      form.addEventListener('submit', () => {
        if (!form.checkValidity()) return;
        const payload = collectPayload(form, formType);
        if (payload) writeStoredPayload(storageKey, payload);
      });

      handleFormResult(form, wrapper);

      if (wrapper.dataset.postedSuccessfully === 'true') {
        const payload = readStoredPayload(storageKey);
        if (payload) sendKlaviyoEvent(payload, storageKey, formType);
      } else {
        readStoredPayload(storageKey);
      }
    });
  };

  const initializeAll = (root = document) => {
    root.querySelectorAll('[data-contact-abok]').forEach(initializeSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
